import axios, { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';
import { RequestQueue } from './RequestQueue';
import { ApiConfig } from '../constants/Constants';
import AuthService from '../services/auth.service';
import { ExceptionHandler, ExtractQueryString, HttpResponseHandler } from '../utils';
import { v4 as uuidv4 } from 'uuid';

axios.defaults.baseURL = ApiConfig.api_base_url;

const CancelToken = axios.CancelToken;
const source = CancelToken.source();
const axiosCancelMessage = 'Operation canceled';
const reqMaps: any = {};
//const myInterceptor =
axios.interceptors.request.use(
  (config: any) => {
    // check request in the requestQueue
    // if exist cancel the existing requests in the requestQueue
    // then put current request to the requestQueue
    // console.log(['config', config]);
    // skip when url is contains "codes", "config"
    const ignoreRoutes = ['upload', 'reader', 'setting'];
    const uuid = uuidv4();
    const url = `${config.method}:${Object.keys(ExtractQueryString(config.url))
      .map((key) => key)
      .join('')}`;

    // create queue if not already exist
    if (!reqMaps[url]) {
      reqMaps[url] = new RequestQueue();
    } else {
      // cancel all previous requests
      // skip when url is contains "codes", "config"
      if (!ignoreRoutes.some((v) => config.url.includes(v))) {
        reqMaps[url].collections.forEach((rq: any) => {
          rq.cancelToken.cancel(`Request with key "${uuid}" and url ${url} was cancelled by interceptors`);
        });

        // empty queue
        reqMaps[url].empty();
      }
    }

    // add to queue
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    reqMaps[url].enqueue({
      key: uuid,
      url: url,
      cancelToken: source,
    });

    config.headers = {
      ...config.headers,
      'X-Requested-Key': uuid, // this key use as a ref key to remove request from the requestQueue
    };

    config.cancelToken = source.token;

    return config;
  },
  (error) => {
    source.cancel(`${axiosCancelMessage} due to response error`);
    return Promise.reject(error);
  },
);

export const HttpRequester = {
  async get(url: string) {
    const config = await this.getConfig();
    try {
      const response = await axios.get(url, config);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  async getFile(url: string, fileName?: string): Promise<void> {
    const config = await this.getConfig();
    const retVal = new Promise<void>((resolve, reject) => {
      try {
        axios
          .get(url, { ...config, responseType: 'blob' })
          .then((response) => {
            // create file link in browser's memory
            const href = URL.createObjectURL(response.data);

            // Use the caller-supplied name first. Only fall back to
            // content-disposition, then to "export-{date}.csv", if none given.
            let resolvedName = fileName;
            if (!resolvedName) {
              const now = new Date();
              resolvedName = `export-${now.getFullYear()}-${now.getMonth()}-${now.getDate()} ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}.csv`;
              const contentDisposition = response.headers['content-disposition'];
              if (contentDisposition) {
                const regex = new RegExp('filename=\\"(.+\\.\\w+)\\"');
                const matches = regex.exec(contentDisposition);
                if (matches && matches.length > 1) resolvedName = matches[1];
              }
            }

            // create anchor element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', resolvedName);
            document.body.appendChild(link);
            link.click();

            // clean up anchor element & remove ObjectURL
            link.remove();
            URL.revokeObjectURL(href);

            resolve();
          })
          .catch((error) => {
            // Without this, a failed download never settles the promise, so the
            // caller's loading spinner would spin forever.
            this.handlerRequestError(error);
            reject(error);
          });
      } catch (error) {
        this.handlerRequestError(error);
        reject(error);
      }
    });

    return retVal;
  },

  async export(url: any, options: any) {
    let config = await this.getConfig();
    if (options) {
      config = {
        ...config,
        ...options,
      };
    }
    try {
      // need to return with header
      return await axios.get(url, { responseType: 'arraybuffer', ...config });
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  async post(url: string, payload: any) {
    const config = await this.getConfig();

    try {
      const response = await axios.post(url, payload, config);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  async postUnauthenticated(url: string, payload: any) {
    try {
      const response = await axios.post(url, payload, undefined);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error, false);
    }
  },

  async put(url: string, payload: any) {
    const config = await this.getConfig();
    try {
      const response = await axios.put(url, payload, config);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  async patch(url: string, payload: any) {
    const config = await this.getConfig();
    try {
      const response = await axios.patch(url, payload, config);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  async delete(url: string, payload?: any) {
    const config = await this.getConfig();

    // Attach payload (if any) to the request config
    if (payload) {
      (config as any).data = payload;
    }

    try {
      const response = await axios.delete(url, config);
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error);
    }
  },

  // The _New methods don't wrap the response in a SingleResult, and throw errors when the response is not successful.
  async get_New(url: string) {
    const config = await this.getConfig();
    return this.simpleRequest(axios.get, url, config);
  },

  async post_New(url: string, body: any, isFormData = false) {
    const config = await this.getConfig();
    if (isFormData) config.headers!['Content-Type'] = 'multipart/form-data';
    return this.simpleRequest(axios.post, url, body, config);
  },

  async put_New(url: string, body: any, isFormData = false) {
    const config = await this.getConfig();
    if (isFormData) config.headers!['Content-Type'] = 'multipart/form-data';
    return this.simpleRequest(axios.put, url, body, config);
  },

  async patch_New(url: string, body: any, isFormData = false) {
    const config = await this.getConfig();
    if (isFormData) config.headers!['Content-Type'] = 'multipart/form-data';
    return this.simpleRequest(axios.patch, url, body, config);
  },

  async delete_New(url: string) {
    const config = await this.getConfig();
    return this.simpleRequest(axios.delete, url, config);
  },

  async simpleRequest(method: (...args: any[]) => Promise<AxiosResponse>, ...args: any[]) {
    try {
      const response = await method(...args);

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const { data, status, headers } = error.response;
          console.log(data, status, headers);
        } else if (error.request) {
          console.log(error.request);
        } else {
          console.log('Error', error.message);
        }
      } else {
        console.error('A non-axios error occurred:', error);
      }

      throw error;
    }
  },

  async uploadFile(url: string, param: any, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const config = await this.getConfig();
    try {
      // Initialize FormData
      const formData = new FormData();

      if (param.images) {
        // For image files, append images as a single field
        formData.append('images', param.images);
      } else if (param.fileProcess) {
        // For other files, append the file with a different key
        formData.append('file', param.fileProcess);
      }

      // Append other parameters to FormData
      if (param) {
        Object.keys(param)
          .filter((key) => key !== 'images' && key !== 'fileProcess')
          .forEach((key) => {
            formData.append(key, param[key]);
          });
      }

      // Set Content-Type to multipart/form-data
      config.headers!['Content-Type'] = 'multipart/form-data';

      // Perform the POST request with the FormData object
      return await axios.post(url, formData, { ...config, onUploadProgress });
    } catch (error) {
      console.error('uploadFile', error);
      return await this.handlerRequestError(error);
    }
  },

  async postPlainText(url: string, data: string) {
    try {
      const response = await axios.post(url, data, {
        headers: {
          'Content-Type': 'text/plain',
        },
      });
      return await HttpResponseHandler.createResponse(response);
    } catch (error) {
      return await this.handlerRequestError(error, false);
    }
  },

  async getConfig(): Promise<AxiosRequestConfig> {
    const config = {
      headers: {
        Authorization: '',
        'Content-Type': 'application/json',
      },
      cancelToken: source.token,
    };

    const isAuthenticated = await AuthService.isAuthenticated();
    if (isAuthenticated) {
      const accessToken = await AuthService.getAccessToken();
      config.headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };
    }
    return config;
  },

  async handlerRequestError(error: any, validateAuthentication?: boolean) {
    console.log(['RequestError', error]);
    if (axios.isCancel(error)) {
      console.log('Request canceled', error);
      return await HttpResponseHandler.createResponse({
        status: 499,
        data: null,
        statusText: 'Canceled',
      });
    } else {
      if (error.response && error.response.status === 401 && validateAuthentication !== false) {
        await AuthService.refreshTokens();
      }
    }

    if ((error.response && error.response.status === 401) || error.message === axiosCancelMessage) {
      // AuthService.logout();
      // window.location.reload();

      return await HttpResponseHandler.createResponse({
        status: 401,
        data: {},
        statusText: error.response?.data?.message || 'Unauthorized',
      });
    }

    if (error.response) return ExceptionHandler.handleHttpError(error.response);
    else
      return await HttpResponseHandler.createResponse({
        status: 500,
        data: {},
        statusText: error.message,
        errorMessage: error.message,
      });
  },

  cancelToken(message?: string) {
    // cancel the request (the message parameter is optional)
    source.cancel(message || 'Operation canceled by the user.');
  },
};
